import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  TrendingUp,
  Target,
  Users,
  Building,
  MapPin,
  Phone,
  Mail,
} from "lucide-react";
import { motion } from "framer-motion";

export const LocalKnowledge = () => {
  const expertise = [
    {
      icon: TrendingUp,
      title: "Market Analysis",
      description:
        "Comprehensive rental market analysis for optimal pricing and positioning.",
    },
    {
      icon: Target,
      title: "Investment Strategy",
      description:
        "Strategic guidance for property acquisition and portfolio growth.",
    },
    {
      icon: Users,
      title: "Tenant Network",
      description:
        "Extensive network of qualified tenants looking for quality rentals.",
    },
    {
      icon: Building,
      title: "Local Partnerships",
      description:
        "Trusted network of contractors, inspectors, and service providers.",
    },
  ];

  const marketSignals = [
    { label: "Property Value Growth", value: 85 },
    { label: "Rental Demand", value: 92 },
    { label: "Tenant Quality", value: 88 },
  ];

  return (
    <section className="py-14 sm:py-18 lg:py-22 bg-secondary/20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-4">
            Johnson County Experts. Local Knowledge. Better Results.
          </h2>
          <p className="text-lg md:text-xl text-foreground/80 max-w-2xl mx-auto font-medium">
            We live, work, and invest in Kansas. We know what works in Overland Park, Leawood, Lenexa, and beyond.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left: Expertise Cards */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="space-y-4"
          >
            <h3 className="font-display text-xl font-semibold text-foreground mb-4">
              Complete Support Services
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {expertise.map((item, index) => {
                const Icon = item.icon;
                return (
                  <Card
                    key={index}
                    className="shadow-[0_10px_30px_rgba(15,42,74,0.06)]"
                  >
                    <CardContent className="p-4">
                      <Icon className="h-6 w-6 text-accent mb-2" />
                      <h4 className="font-display font-semibold text-sm mb-1">
                        {item.title}
                      </h4>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </motion.div>

          {/* Right: Market Signals & Contact */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            {/* Market Signals */}
            <Card className="shadow-[0_10px_30px_rgba(15,42,74,0.06)]">
              <CardContent className="p-6 space-y-4">
                <h3 className="font-display text-lg font-semibold text-foreground mb-4">
                  Market Strength Indicators
                </h3>
                {marketSignals.map((signal, index) => (
                  <div key={index} data-testid="local-knowledge-signal">
                    <div className="flex justify-between mb-2">
                      <span className="text-sm text-foreground/80">
                        {signal.label}
                      </span>
                      <Badge variant="secondary" className="text-xs">
                        {signal.value}%
                      </Badge>
                    </div>
                    <Progress value={signal.value} className="h-2" />
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Contact Info */}
            <Card className="shadow-[0_10px_30px_rgba(15,42,74,0.06)] bg-accent/5">
              <CardContent className="p-6 space-y-3">
                <h3 className="font-display text-lg font-semibold text-foreground mb-4">
                  Contact Our Local Office
                </h3>
                <div className="flex items-start gap-3 text-sm">
                  <MapPin className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium">6850 College Blvd</div>
                    <div className="text-muted-foreground">
                      Overland Park, KS 66211
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Phone className="h-5 w-5 text-accent flex-shrink-0" />
                  <a
                    href="tel:913.795.1155"
                    className="font-medium hover:text-accent transition-colors"
                  >
                    913.795.1155
                  </a>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Mail className="h-5 w-5 text-accent flex-shrink-0" />
                  <a
                    href="mailto:info@rently.properties"
                    className="font-medium hover:text-accent transition-colors"
                  >
                    info@rently.properties
                  </a>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default LocalKnowledge;
